import type { EditorHost } from '@blocksuite/block-std';
import type {
  AffineAIPanelState,
  AffineAIPanelWidget,
  AIItemGroupConfig,
  EdgelessCopilotWidget,
  EdgelessElementToolbarWidget,
  EdgelessRootBlockComponent,
  EdgelessRootService,
} from '@blocksuite/blocks';
import {
  EdgelessCopilotToolbarEntry,
  MindmapElementModel,
} from '@blocksuite/blocks';
import { noop } from '@blocksuite/global/utils';
import type { PiqueMindmapElementModel } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/mindmap-element';
import { readProxyArrayValue } from '@pique/core/modules/properties/services/adapter';
import type {
  SerializedAttendee,
  WorkspaceAffineProperties,
} from '@pique/core/modules/properties/services/schema';
import { PageSystemPropertyId } from '@pique/core/modules/properties/services/schema';
import { html, nothing, type PropertyValues } from 'lit';

import { edgelessActionGroups } from '../../_common/ai-handler';
import {
  getPageActionItems,
  isMindmapChild,
  mindmapChildAttendeeShowWhen,
} from '../../utils/edgeless';
import { renderAttendeeActionsButton } from './attendee-actions-panel';

const INSERT_BELOW_RESPONSE_NAME = 'Insert chat into my notes';

noop(EdgelessCopilotToolbarEntry);

export function setupEdgelessElementToolbarEntry(
  widget: EdgelessElementToolbarWidget
) {
  // Pique Update - Add CSS to hide specific toolbar elements and style the toolbar
  if (widget.shadowRoot) {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      editor-toolbar editor-icon-button[aria-label="Draw connector"],
      editor-toolbar edgeless-change-note-button,
      editor-toolbar edgeless-more-button,
      editor-toolbar editor-toolbar-separator {
        display: none;
      }

      /* Higher border radius for prettier toolbar */
      editor-toolbar {
        border-radius: 16px !important;
      }
    `;
    widget.shadowRoot.append(styleElement);
  }

  widget.registerEntry({
    when: () => {
      return false; // Pique Update - Hide the 'Ask AI' copilot toolbar
    },
    render: (edgeless: EdgelessRootBlockComponent) => {
      const chain = edgeless.service.std.command.chain();
      const filteredGroups = edgelessActionGroups.reduce((pre, group) => {
        const filtered = group.items.filter(item =>
          item.showWhen?.(chain, 'edgeless', edgeless.host)
        );

        if (filtered.length > 0) pre.push({ ...group, items: filtered });

        return pre;
      }, [] as AIItemGroupConfig[]);

      if (filteredGroups.every(group => group.items.length === 0)) return null;

      return html`<edgeless-copilot-toolbar-entry
        .edgeless=${edgeless}
        .host=${edgeless.host}
        .groups=${edgelessActionGroups}
      ></edgeless-copilot-toolbar-entry>`;
    },
  });

  // Register attendee button for mindmap child nodes that match attendees
  widget.registerEntry({
    when: () => {
      return mindmapChildAttendeeShowWhen(null, null, widget.host);
    },
    render: (edgeless: EdgelessRootBlockComponent) => {
      // Get the matching attendee for the selected node
      const getMatchingAttendee = () => {
        try {
          const edgelessService = edgeless.service as EdgelessRootService;
          const selected = edgelessService.selection.selectedElements;
          if (selected.length !== 1) return null;

          const selectedElement = selected[0];
          if (!selectedElement) return null;

          const groupId = selectedElement.group?.id;
          if (!groupId) return null;

          const parentMindmap = edgelessService.getElementById(groupId);

          if (parentMindmap instanceof MindmapElementModel) {
            const nodeMap = parentMindmap.nodeMap;
            if (nodeMap?.has(selectedElement.id)) {
              const node = nodeMap.get(selectedElement.id);
              const nodeElement = node?.element as { text?: { toString(): string } } | undefined;
              if (nodeElement?.text) {
                const nodeText = nodeElement.text.toString().trim();

                const propertiesProxy =
                  edgeless.host.doc.collection.doc.getMapProxy<
                    'affine:workspace-properties',
                    WorkspaceAffineProperties
                  >('affine:workspace-properties');
                const pageProperties =
                  propertiesProxy.pageProperties?.[edgeless.host.doc.id];
                const attendees = readProxyArrayValue<SerializedAttendee>(
                  pageProperties?.system[PageSystemPropertyId.Attendees]
                    ?.value
                );

                return attendees.find(
                  (attendee: SerializedAttendee) =>
                    attendee.email === nodeText || attendee.name === nodeText
                );
              }
            }
          }
        } catch (err) {
          console.error('Error getting matching attendee:', err);
        }
        return null;
      };

      const matchingAttendee = getMatchingAttendee();
      if (!matchingAttendee) return null;

      // Get action items for this attendee
      const actionItems = getPageActionItems(edgeless.host);

      const result = renderAttendeeActionsButton(
        matchingAttendee,
        actionItems,
        edgeless
      );
      return result === nothing ? null : result;
    },
  });
}

const instrumentedEdgelessServices = new WeakSet<EdgelessRootService>();
const dragTrackingState = new WeakMap<
  EdgelessRootService,
  { isDragging: boolean; dragStartTime: number }
>();

function setupMindmapInteractions(
  edgeless: EdgelessRootService,
  _host: EditorHost
) {
  if (instrumentedEdgelessServices.has(edgeless)) return;
  instrumentedEdgelessServices.add(edgeless);

  // Initialize drag tracking state for this service
  dragTrackingState.set(edgeless, { isDragging: false, dragStartTime: 0 });

  const viewport = edgeless.viewport as unknown as { viewportElement?: Element };
  const viewportElement =
    viewport.viewportElement ||
    document.querySelector('affine-edgeless-root');

  if (viewportElement) {
    viewportElement.addEventListener('pointerdown', () => {
      const state = dragTrackingState.get(edgeless);
      if (state) {
        state.dragStartTime = Date.now();
      }
    });

    viewportElement.addEventListener('pointermove', (evt) => {
      const e = evt as PointerEvent;
      if (e.buttons > 0) {
        const state = dragTrackingState.get(edgeless);
        if (state && Date.now() - state.dragStartTime > 50) {
          state.isDragging = true;
        }
      }
    });

    // Track when dragging ends
    viewportElement.addEventListener('pointerup', () => {
      const state = dragTrackingState.get(edgeless);
      if (state) {
        // Reset after a small delay to allow selection to complete
        setTimeout(() => {
          state.isDragging = false;
          state.dragStartTime = 0;
        }, 100);
      }
    });
  }

  // Track pending click handlers to cancel on double-click
  const pendingClickHandlers = new Map<string, NodeJS.Timeout>();

  // 1) React to selection updates (node selection)
  edgeless.selection.slots.updated.on(() => {
    try {
      // Skip if dragging is in progress
      const state = dragTrackingState.get(edgeless);
      if (state?.isDragging) return;

      const selected = edgeless.selection.selectedElements;
      if (selected.length !== 1) return;
      const element = selected[0];
      if (!element || !isMindmapChild(element)) return;

      const isEditing = edgeless.selection.editing || false;
      if (isEditing) return;

      // Cancel any pending handler for this element (might be part of double-click)
      if (pendingClickHandlers.has(element.id)) {
        clearTimeout(pendingClickHandlers.get(element.id));
        pendingClickHandlers.delete(element.id);
      }

      // Delay execution to check if it's a double-click
      const timeoutId = setTimeout(() => {
        pendingClickHandlers.delete(element.id);

        const isNowEditing = edgeless.selection.editing || false;
        if (isNowEditing) return;

        const elementWithGroup = element as { group?: { id?: string } };
        const parent = edgeless.getElementById(elementWithGroup.group?.id ?? '');
        if (
          parent instanceof MindmapElementModel &&
          'handleMindmapNodeClick' in parent
        ) {
          (
            parent as unknown as PiqueMindmapElementModel
          ).handleMindmapNodeClick(element.id);
        }
      }, 250); // Wait 250ms to see if double-click happens

      pendingClickHandlers.set(element.id, timeoutId);
    } catch (err) {
      console.error('Mindmap selection handler error:', err);
    }
  });
}

export function setupEdgelessCopilot(widget: EdgelessCopilotWidget) {
  widget.groups = edgelessActionGroups;
  setupMindmapInteractions(widget.edgeless.service, widget.host);

  if (widget.shadowRoot) {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
    .copilot-selection-rect {
      border: 2px dashed transparent !important;
    }
  `;
    widget.shadowRoot.append(styleElement);
  }
}

type AIPanelWithProtected = AffineAIPanelWidget & {
  willUpdate?: (changed: PropertyValues) => void;
};

type ResponseItem = { name: string; handler?: (host: EditorHost) => void };

export function setupEdgelessAIPanel(widget: AffineAIPanelWidget) {
  const widgetWithProtected = widget as AIPanelWithProtected;
  const originalWillUpdate = widgetWithProtected.willUpdate;

  widgetWithProtected.willUpdate = function (changed: PropertyValues) {
    originalWillUpdate?.call(this, changed);

    const self = this as AffineAIPanelWidget;
    const prevState = changed.get('state') as AffineAIPanelState | undefined;
    if (prevState === 'generating' && self.state !== 'generating') {
      self.style.background = '';
      self.style.border = '';
      self.style.boxShadow = '';
    }

    if (
      changed.has('state') &&
      self.state === 'finished' &&
      self.answer
    ) {
      const responses = self.config?.finishStateConfig?.responses;
      const items = responses?.[0]?.items as ResponseItem[] | undefined;
      const insertBelowResponse = items?.find(
        response => response.name === INSERT_BELOW_RESPONSE_NAME
      );
      if (insertBelowResponse?.handler) {
        const handlerFn = insertBelowResponse.handler;
        setTimeout(() => {
          handlerFn(widget.host);
        }, 100);
      }
    }
  };

  const originalRender = widget.render;

  widget.render = function () {
    const self = this as AffineAIPanelWidget;

    if (self.state === 'generating') {
      self.style.background = 'transparent';
      self.style.border = 'none';
      self.style.boxShadow = 'none';
      self.style.color = 'transparent';

      return html`
        <style>
          .pique-spinner-container {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 16px;
            min-width: 50px;
            min-height: 50px;
          }
          .pique-spinner {
            border: 3px solid var(--affine-white-30);
            border-radius: 50%;
            border-top-color: var(--affine-primary-color);
            width: 20px;
            height: 20px;
            animation: pique-spin 1s linear infinite;
          }
          @keyframes pique-spin {
            to {
              transform: rotate(360deg);
            }
          }
          /* Styles for the answer part when streaming */
          .pique-generating-content {
            /* Pique Update: Add padding/styling for direct answer rendering */
            padding: 0 12px; /* Match padding of original .answer */
            color: var(--affine-text-primary-color); /* Restore text color */
            display: flex;
            flex-direction: column;
            width: 100%; /* Take full width of the potentially visible panel */
            gap: 8px; /* Add gap between potential answer and spinner */
          }
        </style>
        <div class="pique-generating-content">
          ${self.answer && self.config?.answerRenderer && self.state
            ? self.config.answerRenderer(self.answer, self.state)
            : nothing}
          ${!self.answer
            ? html`<div class="pique-spinner-container">
                <div class="pique-spinner"></div>
              </div>`
            : nothing}
        </div>
      `;
    } else if (self.state === 'error') {
      // Render nothing for the error state. Do not change this.
      return nothing;
    } else {
      // Restore default styles when not generating or error
      self.style.background = '';
      self.style.border = '';
      self.style.boxShadow = '';
      self.style.color = ''; // Restore text color
      // Call original render for other states (like 'finished', 'input', 'hidden')
      return originalRender.call(self);
    }
  };
}
